import React, { useState } from "react";
import {
  Container,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from "reactstrap";
import ReactMarkdown from "react-markdown";
import { MarkGithubIcon } from "@primer/octicons-react";

import useApi, { post } from "~/hooks/use-api";
import Button from "~/components/Button";
import BuildCard, { Build } from "~/components/BuildCard";

interface Release {
  name: string;
  version: string;
  gitTag: string;
  changelog: string;
  url: string;
}

export default function ReleasesPage() {
  return (
    <Container className="mt-4">
      <h1>Releases</h1>
      <ReleasesList />
    </Container>
  );
}

function ReleasesList() {
  const { data: releases } = useApi<Release[]>("/api/release");
  const [showBuildsVersion, setShowBuildsVersion] = useState<string>();

  if (!releases) {
    return <Spinner />;
  }

  return (
    <>
      {releases.map((r) => (
        <div className="py-3 border-top" key={r.gitTag}>
          <h2 id={r.gitTag}>
            <a href={`#${r.gitTag}`}>{r.name}</a>{" "}
            <a href={r.url}>
              <MarkGithubIcon verticalAlign="middle" size={24} />
            </a>
            <span className="float-right">
              <Button onClick={() => setShowBuildsVersion(r.version)}>
                Builds
              </Button>
            </span>
          </h2>
          <p>
            <VersionCounts version={r.version} />
          </p>
          <ReactMarkdown>
            {r.changelog.replace(
              /#(\d+)/g,
              "[#$1](https://github.com/LOKE/app/pull/$1)"
            )}
          </ReactMarkdown>
        </div>
      ))}
      <BuildAllModal
        version={showBuildsVersion}
        onClose={() => setShowBuildsVersion(undefined)}
      />
    </>
  );
}

interface AppStats {
  iosAppVersion?: string;
  androidAppVersion?: string;
}

function BuildAllModal(props: { version?: string; onClose(): void }) {
  const { data: stable } = useApi<Release>("/api/release/stable");
  const {
    data: builds,
    revalidate,
    isValidating,
  } = useApi<Build[]>(
    props.version ? `/api/release/${props.version}/builds` : null
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const buildable = stable ? stable.version === props.version : false;

  async function buildAll() {
    if (submitting) {
      return;
    }

    setSubmitting(true);

    try {
      await post<unknown, Build[]>(
        `/api/release/${props.version}/build-all`,
        {}
      );
      revalidate();
    } catch (err) {
      const e = err as Error;
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal size="lg" isOpen={Boolean(props.version)} toggle={props.onClose}>
      <ModalHeader toggle={props.onClose}>{props.version} Builds</ModalHeader>
      <ModalBody>
        {builds && (
          <>
            {builds.map((b) => (
              <BuildCard key={b.id} build={b} />
            ))}
            {buildable && <hr />}
          </>
        )}
        {!builds && isValidating ? <Spinner /> : null}

        {buildable && (
          <Alert color="warning">
            <strong>Warning: Build all</strong> will create a build for every
            app with <em>Start new version builds automatically</em> selected
          </Alert>
        )}

        {error && (
          <Alert color="danger">
            <strong>Error:</strong> {error}
          </Alert>
        )}
      </ModalBody>
      {buildable && (
        <ModalFooter>
          <Button onClick={props.onClose}>Cancel</Button>
          <Button primary onClick={buildAll} disabled={submitting}>
            Build All
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
}

function VersionCounts(props: { version: string }) {
  const { data: appStats } = useApi<AppStats[]>("/api/appstats");

  if (!appStats) return null;

  const { android, ios } = appStats.reduce(
    (acc, app) => {
      return {
        android:
          acc.android + (app.androidAppVersion === props.version ? 1 : 0),
        ios: acc.ios + (app.iosAppVersion === props.version ? 1 : 0),
      };
    },
    {
      android: 0,
      ios: 0,
    }
  );

  return (
    <small>
      Currently <strong>{android}</strong> Android and <strong>{ios}</strong>{" "}
      iOS apps are on this version
    </small>
  );
}
